/**
 * Build Section Tool Schema
 * 
 * SHARED SCHEMA for building complete slide sections with shapes,
 * text, tables, charts, and connectors.
 * 
 * Used by:
 * - Live Slide Editor Agent
 * - Add Slides Agent (future)
 * 
 * Key difference from createOrEdit tools:
 * - Uses paragraphs[] array (not paragraphOperations[])
 * - Designed for creation, not editing
 * - Metadata is a simple array of entries
 */

import { z } from "zod";

// ═══════════════════════════════════════════════════════════════════════════
// SHARED SCHEMAS (Position, Styling, Text)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Position schema for shape placement
 */
export const PositionSchema = z.object({
    left: z.number().optional(),
    top: z.number().optional(),
    width: z.number().optional(),
    height: z.number().optional(),
    right: z.number().optional(),
    bottom: z.number().optional(),
}).describe("Position and dimensions for shape placement");

/**
 * Target area schema
 */
export const TargetAreaSchema = z.object({
    left: z.number(),
    top: z.number(),
    width: z.number(),
    height: z.number(),
    right: z.number().optional(),
    bottom: z.number().optional(),
}).describe("Target area bounds");

/**
 * Fill schema for solid or image fills
 */
export const FillSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("solid"),
        color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
        transparency: z.number().min(0).max(1).optional(),
        null: z.boolean().optional(),
    }),
    z.object({
        type: z.literal("image"),
        imageSource: z.string(),
        fillMode: z.enum(["stretch", "tile", "center"]).optional(),
        transparency: z.number().min(0).max(1).optional(),
    }),
]).describe("Fill styling. Default is transparent (no visible fill). Set a fill explicitly when using light text or when a visible background is required.");

/**
 * Outline schema
 */
export const OutlineSchema = z.object({
    null: z.boolean().optional(),
    color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).nullable().optional(),
    weight: z.number().min(0).optional(),
    dashStyle: z.enum(["solid", "dash", "dot", "dashDot", "longDash"]).optional(),
    transparency: z.number().min(0).max(1).optional(),
}).describe("Outline/border styling. Default is off. Use sparingly for emphasis or separation.");

/**
 * Shadow schema
 */
export const ShadowSchema = z.object({
    visible: z.boolean().optional(),
    color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
    transparency: z.number().min(0).max(1).optional(),
    blurPoints: z.number().min(0).optional(),
    offsetXPoints: z.number().optional(),
    offsetYPoints: z.number().optional(),
    sizePercent: z.number().min(0).max(200).optional(),
}).describe("Shadow styling. Default is off; only enable if absolutely necessary.");

/**
 * Inherit properties source schema
 */
export const InheritSourceSchema = z.object({
    componentId: z.string().optional(),
    slideId: z.number().int().optional(),
    shapeId: z.number().int(),
}).describe("Source for inheriting properties");

/**
 * Special text run schema
 */
export const TextFormatSchema = z.object({
    text: z.string().describe("Text that must exist in the paragraph"),
    fontFamily: z.string().optional(),
    fontSize: z.number().min(1).optional(),
    bold: z.boolean().optional(),
    italic: z.boolean().optional(),
    underline: z.boolean().optional(),
    strikethrough: z.boolean().optional(),
    textColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
    highlight: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
    superscript: z.boolean().optional(),
    subscript: z.boolean().optional(),
}).describe("Run-level formatting for a text segment");

/**
 * Bullet schema
 */
export const BulletSchema = z.union([
    z.literal("none"),
    z.object({
        type: z.literal("character"),
        character: z.number().int(),
        font: z.string().optional(),
        color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
    }),
    z.object({
        type: z.literal("numbered"),
        style: z.number().int().min(0).max(15).optional(),
        start_at: z.number().int().optional(),
    }),
]).describe("Bullet styling");

/**
 * Inherit formatting from an existing paragraph
 */
export const InheritFormattingFromSchema = z.object({
    component_id: z.string().optional(),
    slide_id: z.number().int().optional(),
    shape_id: z.number().int(),
    table_cell: z.object({
        row: z.number().int().min(1),
        col: z.number().int().min(1),
    }).optional(),
    paragraph_index: z.number().int(),
    properties: z.array(z.string()).optional(),
}).describe("Copy formatting from a template paragraph (use for mixed styling).");

/**
 * Shared paragraph payload (used by buildArea and live editor operations)
 */
export const ParagraphPayloadSchema = z.object({
    setText: z.string().optional().describe("Full paragraph text (use this; replaces paragraph content)."),
    replaceText: z.object({
        find: z.string(),
        replace: z.string(),
    }).optional().describe("Find/replace within the paragraph (preserves other content)."),
    inheritFormattingFrom: InheritFormattingFromSchema.optional(),
    bullet: BulletSchema.optional().describe("Bullet styling (or 'none')."),
    paragraphAlignment: z.enum(["left", "center", "right", "justify", "distributed"]).optional().describe("Paragraph alignment."),
    spaceBefore: z.number().min(0).optional().describe("Spacing before the paragraph."),
    spaceAfter: z.number().min(0).optional().describe("Spacing after the paragraph."),
    lineSpacing: z.number().min(0).optional().describe("Line spacing for the paragraph."),
    indentLevel: z.number().int().min(0).max(5).optional().describe("Indent level (0-5)."),
    beforeTextIndent: z.number().min(0).optional().describe("Indent before paragraph text."),
    hangingIndent: z.number().optional().describe("Hanging indent value."),
    textFormat: z.array(TextFormatSchema).optional()
        .describe("Run-level styling for mixed formatting (required when setting or replacing text)."),
}).superRefine((data, ctx) => {
    if ((data.setText || data.replaceText) && (!data.textFormat || data.textFormat.length === 0)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "textFormat is required when setText or replaceText is provided to preserve run styling.",
        });
    }
});

/**
 * Paragraph schema for buildArea (uses paragraphs[] not operations)
 */
export const ParagraphSchema = ParagraphPayloadSchema.describe("Paragraph definition for buildArea");

/**
 * Text content schema (build-only, uses paragraphs[])
 */
export const TextContentSchema = z.object({
    keepExisting: z.boolean().optional(),
    paragraphs: z.array(ParagraphSchema).optional(),
}).describe("Text content with paragraphs array");

/**
 * Text frame schema
 */
export const TextFrameSchema = z.object({
    inheritPropertiesFrom: z.object({
        componentId: z.string().optional(),
        slideId: z.number().int().optional(),
        shapeId: z.number().int(),
        tableCell: z.object({
            row: z.number().int().min(1),
            col: z.number().int().min(1),
        }).optional(),
        properties: z.array(z.string()).optional(),
    }).optional(),
    cols: z.number().int().min(1).optional().describe("Number of text columns."),
    colSpacing: z.number().min(0).optional().describe("Spacing between columns."),
    topMargin: z.number().min(0).optional()
        .describe("Top text margin in points (overridden by topMarginPercent)."),
    leftMargin: z.number().min(0).optional()
        .describe("Left text margin in points (overridden by leftMarginPercent)."),
    bottomMargin: z.number().min(0).optional()
        .describe("Bottom text margin in points (overridden by bottomMarginPercent)."),
    rightMargin: z.number().min(0).optional()
        .describe("Right text margin in points (overridden by rightMarginPercent)."),
    topMarginPercent: z.number().min(0).max(100).optional()
        .describe("Top text margin as % of shape height (default 12% if no margins provided)."),
    leftMarginPercent: z.number().min(0).max(100).optional()
        .describe("Left text margin as % of shape width (default 6% if no margins provided)."),
    bottomMarginPercent: z.number().min(0).max(100).optional()
        .describe("Bottom text margin as % of shape height (default 12% if no margins provided)."),
    rightMarginPercent: z.number().min(0).max(100).optional()
        .describe("Right text margin as % of shape width (default 6% if no margins provided)."),
    horizontalAlignment: z.enum(["left", "center", "right", "justify"]).optional()
        .describe("Horizontal alignment (center for labels, left for most body text)."),
    verticalAlignment: z.enum(["top", "middle", "bottom"]).optional()
        .describe("Vertical alignment (top for long-form text, middle for labels)."),
    rotateText: z.enum(["horizontal", "90", "270"]).optional(),
    autofit: z.enum(["none", "shrink_on_overflow", "resize_shape"])
        .optional()
        .default("shrink_on_overflow")
        .describe("Autofit behavior (default: 'shrink_on_overflow' - shrink text to fit shape)."),
    wordWrap: z.boolean().optional(),
}).describe("Text frame properties");

/**
 * Unified metadata entry schema (simple array for buildSection)
 */
export const MetadataEntrySchema = z.object({
    type: z.string()
        .describe("Entry type (artifact, citation, or future types)"),
    sourceContextId: z.string()
        .describe("Context ID for the original source (as_*, page_*, excelread_*, artifact_*)"),
    payload: z.record(z.string(), z.any()).optional()
        .describe("Inline payload for this entry (stored in ContextResolver)"),
}).describe("Unified metadata entry");

// ═══════════════════════════════════════════════════════════════════════════
// TABLE SCHEMAS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Cell reference schema
 */
export const CellRefSchema = z.object({
    row: z.number().int().min(1),
    col: z.number().int().min(1),
}).describe("1-based table cell reference (row, col).");

/**
 * Cell range schema
 */
export const CellRangeSchema = z.object({
    startRow: z.number().int().min(1),
    startCol: z.number().int().min(1),
    endRow: z.number().int().min(1),
    endCol: z.number().int().min(1),
}).describe("1-based table cell range (inclusive).");

/**
 * Cell text entry schema
 */
export const CellTextSchema = z.object({
    cell: CellRefSchema,
    textFrame: TextFrameSchema.optional(),
    textContent: TextContentSchema.optional(),
}).describe("Cell text content (defaults: only row 1/col 1 middle+center, other cells middle+left; text black unless styled).");

/**
 * Border schema
 */
export const BorderSchema = z.object({
    color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
    weight: z.number().min(0).optional(),
    dashStyle: z.enum(["solid", "dash", "dot", "dashDot", "longDash"]).optional(),
    transparency: z.number().min(0).max(1).optional(),
    null: z.boolean().optional(),
});

/**
 * Cell formatting schema
 */
export const CellFormattingSchema = z.object({
    cellRange: CellRangeSchema,
    copyFormatFrom: z.object({
        componentId: z.string().optional(),
        slideId: z.number().int().optional(),
        tableShapeId: z.number().int(),
        sourceCell: CellRefSchema.optional(),
        sourceCellRange: CellRangeSchema.optional(),
        properties: z.array(z.enum(["fill", "borderTop", "borderBottom", "borderLeft", "borderRight", "borders"])).optional(),
    }).optional(),
    explicitFormatting: z.object({
        fill: z.object({
            type: z.literal("solid"),
            color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
            transparency: z.number().min(0).max(1).optional(),
            null: z.boolean().optional(),
        }).optional(),
        borderTop: BorderSchema.optional(),
        borderBottom: BorderSchema.optional(),
        borderLeft: BorderSchema.optional(),
        borderRight: BorderSchema.optional(),
    }).optional(),
}).describe("Cell formatting (fills and borders are blank by default; set explicitFormatting to show them).");

/**
 * Table config schema for buildSection (creation only)
 */
export const TableConfigSchema = z.object({
    rows: z.number().int().min(1),
    columns: z.number().int().min(1),
    columnWidths: z.array(z.number().min(0)).optional(),
    rowHeights: z.array(z.number().min(0)).optional(),
    mergeCells: z.array(CellRangeSchema).optional(),
    cellText: z.array(CellTextSchema).optional(),
    cellFormatting: z.array(CellFormattingSchema).optional(),
});

// ═══════════════════════════════════════════════════════════════════════════
// CHART SCHEMAS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Chart types enum
 */
export const ChartTypeEnum = z.enum([
    "column", "column_stacked", "column_stacked_100",
    "bar", "bar_stacked", "bar_stacked_100",
    "line", "line_markers", "line_stacked", "line_stacked_100",
    "area", "area_stacked", "area_stacked_100",
    "pie", "doughnut",
    "scatter", "scatter_smooth", "scatter_smooth_markers",
    "scatter_straight", "scatter_straight_markers",
    "bubble", "stock_ohlc", "stock_vhlc",
    "radar", "radar_filled", "waterfall", "funnel",
    "treemap", "sunburst", "histogram", "box_whisker", "combo"
]);

/**
 * Data generator schema
 */
export const DataGeneratorSchema = z.object({
    type: z.enum(["numeric_sequence", "date_sequence", "text_pattern"]),
    start: z.union([z.number(), z.string()]).optional(),
    end: z.union([z.number(), z.string()]).optional(),
    step: z.union([z.number(), z.string()]).optional(),
    count: z.number().int().optional(),
});

/**
 * Series data schema
 */
export const SeriesSchema = z.object({
    name: z.string(),
    values: z.array(z.union([z.number(), z.null()])).optional(),
    generator: DataGeneratorSchema.optional(),
    formatCode: z.string().optional(),
    chartType: z.string().optional(),
    axisGroup: z.enum(["primary", "secondary"]).optional(),
    fill: z.object({
        color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).nullable().optional(),
        transparency: z.number().min(0).max(1).optional(),
    }).optional(),
    outline: z.object({
        color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).nullable().optional(),
        weight: z.number().min(0).optional(),
        dashStyle: z.enum(["solid", "dash", "dot", "dashDot", "longDash"]).optional(),
    }).optional(),
    line: z.object({
        color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
        weight: z.number().min(0).optional(),
        dashStyle: z.enum(["solid", "dash", "dot", "dashDot", "longDash"]).optional(),
    }).optional(),
    marker: z.object({
        style: z.enum(["automatic", "circle", "square", "diamond", "triangle", "star", "x", "plus", "dash", "dot", "picture", "none"]).optional(),
        size: z.number().int().min(2).max(72).optional(),
        fill: z.object({ color: z.string().optional(), transparency: z.number().optional() }).optional(),
        outline: z.object({ color: z.string().optional(), weight: z.number().optional() }).optional(),
    }).optional(),
    dataLabels: z.object({
        show: z.boolean().optional(),
        position: z.enum(["center", "inside_end", "inside_base", "outside_end", "left", "right", "top", "bottom", "above", "below", "best_fit"]).optional(),
        formatCode: z.string().optional(),
        textFormat: z.object({
            fontFamily: z.string().optional(),
            fontSize: z.number().optional(),
            bold: z.boolean().optional(),
            italic: z.boolean().optional(),
            underline: z.boolean().optional(),
            color: z.string().optional(),
        }).optional(),
        autoContrast: z.boolean().optional(),
    }).optional(),
    dataPoints: z.array(z.object({
        index: z.number().int().min(0),
        fill: z.object({ color: z.string().optional(), transparency: z.number().optional() }).optional(),
        outline: z.object({ color: z.string().optional(), weight: z.number().optional() }).optional(),
        dataLabel: z.object({ show: z.boolean().optional(), formatCode: z.string().optional() }).optional(),
    })).optional(),
});

/**
 * Axis schema
 */
export const AxisSchema = z.object({
    bounds: z.object({ minimum: z.number().optional(), maximum: z.number().optional() }).optional(),
    units: z.object({ major: z.number().optional(), minor: z.number().optional() }).optional(),
    crosses: z.object({ type: z.enum(["auto", "maximum", "minimum", "custom"]).optional(), value: z.number().optional() }).optional(),
    displayUnits: z.enum(["none", "hundreds", "thousands", "ten_thousands", "hundred_thousands", "millions", "ten_millions", "hundred_millions", "billions", "trillions"]).optional(),
    logarithmicScale: z.boolean().optional(),
    logBase: z.number().min(2).optional(),
    valuesInReverseOrder: z.boolean().optional(),
    tickMarks: z.object({ majorType: z.string().optional(), minorType: z.string().optional() }).optional(),
    labels: z.object({
        position: z.string().optional(),
        formatCode: z.string().optional(),
        rotationAngle: z.number().optional(),
        labelOffset: z.number().optional(),
    }).optional(),
    title: z.object({
        text: z.string().optional(),
        rotationAngle: z.number().optional(),
        textFormat: z.object({ fontFamily: z.string().optional(), fontSize: z.number().optional(), bold: z.boolean().optional(), italic: z.boolean().optional(), underline: z.boolean().optional(), color: z.string().optional() }).optional(),
    }).optional(),
    textFormat: z.object({ fontFamily: z.string().optional(), fontSize: z.number().optional(), bold: z.boolean().optional(), italic: z.boolean().optional(), underline: z.boolean().optional(), color: z.string().optional() }).optional(),
    categoryAxisType: z.enum(["auto", "text", "date"]).optional(),
    axisBetweenCategories: z.boolean().optional(),
    gridlines: z.object({
        major: z.object({ show: z.boolean().optional(), color: z.string().optional(), weight: z.number().optional(), dashStyle: z.string().optional() }).optional(),
        minor: z.object({ show: z.boolean().optional(), color: z.string().optional(), weight: z.number().optional(), dashStyle: z.string().optional() }).optional(),
    }).optional(),
});

/**
 * Chart config schema for buildSection (creation only)
 */
export const ChartConfigSchema = z.object({
    chartType: ChartTypeEnum,
    barColumnOptions: z.object({
        gapWidth: z.number().int().min(0).max(500).optional(),
        seriesOverlap: z.number().int().min(-100).max(100).optional(),
    }).optional(),
    pieOptions: z.object({
        rotation: z.number().int().min(0).max(360).optional(),
        holeSize: z.number().int().min(10).max(90).optional(),
        explosion: z.number().int().min(0).max(400).optional(),
    }).optional(),
    lineOptions: z.object({
        smoothLines: z.boolean().optional(),
    }).optional(),
    data: z.object({
        categories: z.object({
            type: z.enum(["string", "number", "date", "auto"]).optional(),
            values: z.array(z.union([z.string(), z.number()])).optional(),
            generator: DataGeneratorSchema.optional(),
            formatCode: z.string().optional(),
        }).optional(),
        series: z.array(SeriesSchema),
    }),
    axes: z.object({
        categoryAxis: AxisSchema.optional(),
        primaryValueAxis: AxisSchema.optional(),
        secondaryValueAxis: AxisSchema.optional(),
    }).optional(),
    legend: z.object({
        position: z.enum(["none", "top", "bottom", "left", "right", "top_right"]).optional(),
        overlay: z.boolean().optional(),
        textFormat: z.object({ fontFamily: z.string().optional(), fontSize: z.number().optional(), bold: z.boolean().optional(), italic: z.boolean().optional(), underline: z.boolean().optional(), color: z.string().optional() }).optional(),
    }).optional(),
    title: z.object({
        show: z.boolean().optional(),
        text: z.string().optional(),
        textFormat: z.object({ fontFamily: z.string().optional(), fontSize: z.number().optional(), bold: z.boolean().optional(), italic: z.boolean().optional(), underline: z.boolean().optional(), color: z.string().optional() }).optional(),
        centered: z.boolean().optional(),
        height: z.number().optional(),
    }).optional(),
    plotArea: z.object({
        fill: z.object({ color: z.string().optional(), transparency: z.number().optional() }).optional(),
        outline: z.object({ color: z.string().optional(), weight: z.number().optional(), dashStyle: z.string().optional() }).optional(),
    }).optional(),
    inheritFrom: z.object({
        chartShapeId: z.number().int(),
        componentId: z.string().optional(),
        slideId: z.number().int().optional(),
        properties: z.array(z.string()).optional(),
        seriesMapping: z.array(z.object({ sourceIndex: z.number().int(), targetIndex: z.number().int() })).optional(),
    }).optional(),
});

// ═══════════════════════════════════════════════════════════════════════════
// CONNECTOR SCHEMA
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Point schema
 */
export const PointSchema = z.object({
    x: z.number(),
    y: z.number(),
});

/**
 * Arrowhead schema
 */
export const ArrowheadSchema = z.object({
    style: z.enum(["none", "triangle", "oval", "stealth", "diamond", "open"]).optional(),
    length: z.enum(["short", "medium", "long"]).optional(),
    width: z.enum(["narrow", "medium", "wide"]).optional(),
});

/**
 * Connector config schema
 */
export const ConnectorConfigSchema = z.object({
    type: z.enum(["straight", "elbow", "curved"]),
    points: z.array(PointSchema).min(2),
    lineStyle: z.object({
        color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
        weight: z.number().min(0.25).optional(),
        dashStyle: z.enum(["solid", "dash", "dot", "dashDot", "longDash"]).optional(),
    }).optional(),
    arrowheadStart: ArrowheadSchema.optional(),
    arrowheadEnd: ArrowheadSchema.optional(),
});

// ═══════════════════════════════════════════════════════════════════════════
// COLLECTION ITEM SCHEMA
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Copy source schema
 */
export const CopySourceSchema = z.object({
    componentId: z.string().optional(),
    slideId: z.number().int().optional(),
    shapes: z.array(z.object({
        shapeId: z.number().int(),
        targetPosition: PositionSchema.optional(),
        textFrame: TextFrameSchema.optional(),
        textContent: TextContentSchema.optional(),
    })).optional(),
    targetArea: TargetAreaSchema.optional(),
});

/**
 * Collection item schema - represents one element in the section
 */
export const CollectionItemSchema = z.object({
    type: z.enum(["copy", "createShape", "createTable", "createChart", "createConnector"]),
    purpose: z.string().optional(),
    notes: z.string().optional(),
    position: PositionSchema.optional(),
    rotation: z.number().optional(),
    zOrder: z.number().int().optional(),
    fill: FillSchema.optional(),
    outline: OutlineSchema.optional(),
    shadow: ShadowSchema.optional(),
    
    // Copy-specific
    copySource: CopySourceSchema.optional(),
    
    // Shape-specific
    shapeType: z.enum(["textbox", "rectangle", "ellipse", "line", "triangle", "arrow"]).optional(),
    inheritShapeProperties: z.object({
        source: InheritSourceSchema,
        properties: z.array(z.enum(["fill", "outline", "shadow", "margins"])),
    }).optional(),
    textFrame: TextFrameSchema.optional(),
    textContent: TextContentSchema.optional(),
    
    // Table-specific
    tableConfig: TableConfigSchema.optional(),
    
    // Chart-specific
    chartConfig: ChartConfigSchema.optional(),
    
    // Connector-specific
    connectorConfig: ConnectorConfigSchema.optional(),
    
    // Metadata (simple array for buildSection)
    metadata: z.array(MetadataEntrySchema).optional(),
});

// ═══════════════════════════════════════════════════════════════════════════
// MAIN BUILD SECTION PARAMS SCHEMA
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Build Section parameters schema
 */
export const BuildSectionParamsSchema = z.object({
    sectionId: z.string()
        .describe("Unique identifier for this section"),
    
    slideId: z.number().int().min(1)
        .describe("Target slide number (1-based)"),
    
    postChartMode: z.boolean()
        .optional()
        .describe("Whether this is post-chart generation mode"),
    
    targetArea: TargetAreaSchema
        .describe("Target area bounds for the entire section"),
    
    collections: z.array(CollectionItemSchema)
        .min(1)
        .describe("Array of shapes/tables/charts/connectors to create"),
});

export type BuildSectionParams = z.infer<typeof BuildSectionParamsSchema>;
export type CollectionItem = z.infer<typeof CollectionItemSchema>;
export type TableConfig = z.infer<typeof TableConfigSchema>;
export type ChartConfig = z.infer<typeof ChartConfigSchema>;
export type ConnectorConfig = z.infer<typeof ConnectorConfigSchema>;

